import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withApollo, graphql } from "react-apollo";
import { push } from 'connected-react-router';
import { Row, Col, Card, Breadcrumb, Spin, Modal } from 'antd';
import AntIcon from '../shared/components/AntIcon';
import { Link } from 'react-router-dom';

import moment from 'moment';
import _ from 'lodash';

import { CreateArticleMutation, ArticleLinksQuery } from './queries';
import { prepareCreateArticleRecord } from './articleRecord';
import { rssDiag, serializeAxiosError } from './rssDiag';
import { newsArticlesLoaded, newsLoadingStarted, newsLoadingFinished, newsArticleAdded } from './newsActions';

import fetch from './feed_fetchers/rss';
import mercury from './plugs/mercury';
import {
  firstImg, withSource, fixHHDImageLinks,
  clearSneakerFilesArticles, removeEbayBlock,
  prependComplexSneakersVideos,
  fixMercuryHTMLSpaces,
} from './plugs/plugs';

const normalizeUrl = (url) => _.trimEnd(url, '/');

/** When changing sources, keep scripts/hourly-news-import.js NEWS_SOURCE_BASE_URLS in sync. */
const NewsSources = [
  {
    link: 'https://sneakerbardetroit.com',
    pipe: [withSource("Sneaker Bar Detroit"), mercury.parse, mercury.useImage, mercury.useContent],
  },
  {
    link: 'https://sneakernews.com',
    pipe: [mercury.parse, mercury.useImage, mercury.useContent, removeEbayBlock],
  },
  {
    link: 'https://solecollector.com/news',
    pipe: [withSource("Sole Collector"), mercury.parse, mercury.useContent, fixMercuryHTMLSpaces],
  },
  {
    link: 'http://www.complex.com/sneakers/',
    pipe: [mercury.parse, mercury.useContent, prependComplexSneakersVideos, fixMercuryHTMLSpaces],
  },
  {
    link: 'http://www.highsnobiety.com/category/footwear/',
    pipe: [withSource("Highsnobiety"), mercury.parse, mercury.useImage, mercury.useContent],
  },
  { link: 'https://www.sneakerfiles.com', pipe: [firstImg, clearSneakerFilesArticles] },
  {
    link: 'http://www.thesneakercrush.com/news/',
    pipe: [withSource("The Sneaker Crush"), mercury.parse, mercury.useImage, mercury.useContent, fixHHDImageLinks],
  },
];

class Sources extends Component {
  constructor(props) {
    super(props);

    this.state = { previewItem: null, columns: 3 };

    this.renderParsedArticles = this.renderParsedArticles.bind(this);
    this.updateColumnsCount = this.updateColumnsCount.bind(this);
    this.startFetching = this.startFetching.bind(this);

    this.addItem = this.addItem.bind(this);
    this.editItem = this.editItem.bind(this);
    this.previewItem = this.previewItem.bind(this);
  }

  updateColumnsCount() {
    const columns = Math.max(1, Math.floor(window.innerWidth / 300));
    if (this.state.columns !== columns) {
      this.setState({ columns });
    }
  }

  componentDidMount() {
    const { isNewsLoaded, newsLoadingStarted, articles } = this.props;
    if (isNewsLoaded && articles == null) {
      newsLoadingStarted();
    }

    this.updateColumnsCount();
    window.addEventListener('resize', this.updateColumnsCount);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateColumnsCount);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { isLoading, isNewsLoaded, newsLoadingStarted, articles } = this.props;
    if (!isNewsLoaded && nextProps.isNewsLoaded && articles == null) {
      newsLoadingStarted();
    }

    if (!isLoading && nextProps.isLoading) {
      this.startFetching();
    }
  }

  startFetching() {
    const { newsUrls, newsArticlesLoaded, newsLoadingFinished } = this.props;
    const urlsSet = new Set(newsUrls);

    rssDiag('info', 'fetch_news_started', {
      sourceCount: NewsSources.length,
      existingArticleLinks: urlsSet.size,
    });

    const feeds = NewsSources.map(s => fetch(s.link)
      .then(items => {
        const results = items
          .filter(item => !urlsSet.has(normalizeUrl(item.link)))
          .map(item => {
            return _.reduce(s.pipe, (pr, fun) => pr.then(fun), Promise.resolve(item))
              .then(item => _.pick(item, ['title', 'link', 'date', 'source', 'content', 'image']))
              .catch((e) => {
                rssDiag('error', 'source_pipe_item_failed', {
                  sourceLink: s.link,
                  itemLink: item && item.link,
                  title: item && item.title,
                  ...serializeAxiosError(e),
                });
                return null;
              });
          });
        return Promise.all(results)
          .then(items => items.filter(i => i))
          .then(newsArticlesLoaded);
      })
      .catch((e) => {
        rssDiag('error', 'source_feed_fetch_failed', {
          sourceLink: s.link,
          ...serializeAxiosError(e),
        });
      })
    );

    Promise.all(feeds).then(() => {
      rssDiag('info', 'fetch_news_feeds_settled', {
        sourceCount: NewsSources.length,
      });
      newsLoadingFinished();
    });
  }

  addItem(item, idx) {
    const { client, newsArticleAdded } = this.props;
    const record = prepareCreateArticleRecord({ ...item });
    client.mutate({ mutation: CreateArticleMutation, variables: { record }})
      .then(() => newsArticleAdded(idx))
      .catch((e) => {
        const graphQLErrors = e.graphQLErrors || (e.networkError && e.networkError.result && e.networkError.result.errors);
        rssDiag('error', 'create_article_mutation_failed', {
          title: record && record.title,
          link: record && record.link,
          message: e.message,
          graphQLErrors: graphQLErrors ? JSON.stringify(graphQLErrors).slice(0, 800) : undefined,
          networkError: e.networkError && (e.networkError.message || String(e.networkError)),
        });
        alert(`Failed to add article: ${e.message}.`);
      });
  }

  editItem(item, idx) {
    this.props.push(`/news/sources/${idx}/edit`);
  }

  previewItem(item) {
    this.setState({ previewItem: item });
  }

  renderParsedArticles() {
    const { columns } = this.state;
    const { articles, addedIndexes } = this.props;

    const colSpan = Math.floor(24 / columns);
    const items = _.map(articles, (item, idx) => ({ item, idx }));

    return (
      <div id="news-grid">
        {_.chunk(items, columns).map((items, idx) => (
          <Row key={idx} gutter={16}>
            {items.map(({ item, idx }) => (
              <Col span={colSpan} key={item.link}>
                <Card
                  cover={<img alt={item.title} src={item.image}/>}
                  actions={addedIndexes.includes(idx) ? [
                    "ARTICLE ADDED"
                  ] : [
                    <AntIcon type="plus" onClick={e => this.addItem(item, idx)} />,
                    <AntIcon type="edit" onClick={e => this.editItem(item, idx)} />,
                    <AntIcon type="search" onClick={e => this.previewItem(item)} />,
                  ]}
                >
                  <p><a href={item.link}>{item.title}</a></p>
                  <Card.Meta
                    title={item.source}
                    description={moment(item.date).format('lll')}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        ))}
      </div>
    );
  }

  render() {
    const { previewItem } = this.state;
    const { isLoading, articles } = this.props;
    const showSpinner = isLoading || articles == null;

    return (
      <div id="news-sources">
        <Breadcrumb>
          <Breadcrumb.Item><Link to="/news">News</Link></Breadcrumb.Item>
          <Breadcrumb.Item>Fetch News</Breadcrumb.Item>
        </Breadcrumb>

        <div className="container">
          {showSpinner ? (<Spin>{this.renderParsedArticles()}</Spin>) : this.renderParsedArticles()}
        </div>

        <Modal
          title={previewItem && previewItem.title}
          open={previewItem != null}
          footer={null}
          onCancel={() => this.setState({ previewItem: null })}
        >
          {previewItem &&
            <div id="sneaker_crush__article" dangerouslySetInnerHTML={{ __html: previewItem.content }} />
          }
        </Modal>
      </div>
    );
  }
}

export default compose(
  graphql(
    ArticleLinksQuery,
    { options: { fetchPolicy: 'no-cache' } },
  ),
  connect(
    ({ news }, { data }) => ({
      ...news,
      isNewsLoaded: !!data.Articles,
      newsUrls: _.map(data.Articles, (a) => normalizeUrl(a.link)),
    }),
    { newsArticlesLoaded, newsLoadingStarted, newsLoadingFinished, newsArticleAdded, push }
  ),
  withApollo,
)(Sources);
